import React, {useContext} from "react";

import cx from "classnames";
import _ from "lodash";
import dateFormat from "dateformat";

import { useDraggable } from "@dnd-kit/core";

import Thumbnail from "src/components/thumbnail";
import Text from "src/components/text";
import { Media } from "src/types/media";
import { StoryboardItem } from "src/types/experiences/storyboards";
import { formatStoryboardType } from "src/utils/experiences.helper";
import {
  buildCleverstoryUrl,
  buildPageTargetPath,
  buildPagePreviewPath,
} from "src/pages/experiences/utils/cleverstoryRedirect";
import { StoryboardsContext, UploadingStoryboard } from "../../context";

import cardStyles from "./storyboardCard.module.scss";
import { useNavigate } from "react-router-dom";
import { STORYBOARD_TYPES } from "src/pages/experiences/types";
import { canEdit, canManage } from "src/pages/experiences/storyboardSettings/utils/permissions";

function buildStoryboardIcon(icon: any): Media | undefined {
  if (!icon?.url) return undefined;
  return {
    id: icon.id,
    type: "image",
    key: icon.url,
  };
}

interface StoryboardCardProps {
  storyboard: StoryboardItem;
  workspaceId: string;
  readOnly?: boolean;
  className?: string;
}

export const StoryboardCard: React.FC<StoryboardCardProps> = ({ storyboard, workspaceId, readOnly = false, className }) => {
  const { editMode, selectedStoryboards, onStoryboardSelect, renameEdits, onRenameChange } = useContext(StoryboardsContext);

  const id = _.get(storyboard, "id");
  const name = _.get(storyboard, "name", "");
  const rawIcon = _.get(storyboard, "page.icon");
  const media = buildStoryboardIcon(rawIcon);
  const type = formatStoryboardType(_.get(storyboard, "page.storyboardType") || _.get(storyboard, "type"));
  const published = _.get(storyboard, "page.published", false);
  const draft = _.get(storyboard, "page.changes", false);
  const pageId = _.get(storyboard, "page.id", null);

  const isSelectionMode = !readOnly && (editMode === "clone" || editMode === "delete");
  const isSelected = selectedStoryboards.some((s) => s.id === id);
  const navigate = useNavigate();

  // Per-storyboard role gating: edit + clone require owner/editor, delete
  // requires owner; preview is open to everyone. Clone/delete run in their
  // respective selection modes, so a card is only selectable when the user may
  // perform that mode's action on it.
  const editable = canEdit(storyboard.role);
  const manageable = canManage(storyboard.role);
  const selectable =
    editMode === "clone"
      ? editable
      : editMode === "delete"
        ? manageable
        : true;

  const { isDragging, attributes, listeners, setNodeRef } = useDraggable({
    id: id || "untitled",
    disabled: readOnly || !!editMode,
    data: {
      storyboard,
      type: "storyboard",
    },
  });

  const handleEditClick = () => {
    if (readOnly || !pageId) return;
    window.open(
      buildCleverstoryUrl(buildPageTargetPath(workspaceId, pageId)),
      "_blank",
    );
  };

  const handlePreviewClick = () => {
    if (readOnly || !pageId) return;
    window.open(
      buildCleverstoryUrl(buildPagePreviewPath(workspaceId, pageId)),
      "_blank",
    );
  };

  const isRenaming = !readOnly && editMode === "rename";

  const handleCardClick = () => {
    if (readOnly) return;
    // In rename mode the card name is an editable input — don't navigate.
    if (isRenaming) return;
    if (isSelectionMode) {
      // Block selecting cards the user can't act on in this mode.
      if (!selectable) return;
      // Always pass the storyboard; the page handler toggles (delete) or
      // replaces (clone). Passing null would clear the whole selection.
      onStoryboardSelect(storyboard);
      return;
    }
    navigate(`/experiences/${workspaceId}/storyboards/${storyboard.id}/website-settings`);
  };

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={cx(cardStyles.card, className, {
        [cardStyles.selected]: isSelected,
        [cardStyles.readonly]: readOnly,
        [cardStyles.dragging]: isDragging,
        [cardStyles.disabled]: isSelectionMode && !selectable,
      })}
      onClick={handleCardClick}
    >
      <div className={cardStyles.icon}>
        <Thumbnail
          media={media}
          defaultImage="/images/collections/background.jpeg"
          width={600}
        />
        <div className={cardStyles.indicatorWrapper}>
          {type && (
            <div className={cardStyles.indicators}>
              {renderStoryBoardIcon(_.get(storyboard, "page.storyboardType") || _.get(storyboard, "type") || "")}
              <span>{type}</span>
            </div>
          )}
          {draft && (
            <div className={cx(cardStyles.indicators, cardStyles.draft)}>
              <i className="paperflite-edit"></i>
              <span>Draft</span>
            </div>
          )}
          {published && (
            <div className={cx(cardStyles.indicators, cardStyles.published)}>
              <i className="paperflite-live"></i>
            </div>
          )}
        </div>

        {/* Selection radio — visible in clone/delete mode, same as CollectionCard */}
        {isSelectionMode && (
          <div className={cardStyles.selection}>
            {isSelected
              ? <i className="fa-solid fa-check-circle" />
              : <i className="paperflite-radio-button-unselected" />
            }
          </div>
        )}

        {/* Hover action bar — only shown when NOT in selection mode and NOT readOnly */}
        {!isSelectionMode && !readOnly && (
          <div className={cardStyles.actions} onClick={(e) => e.stopPropagation()}>
            {editable && (
              <>
                <button
                  className={cardStyles.actionBtn}
                  onClick={handleEditClick}
                >
                  <i className="paperflite-edit" />
                </button>
                <div className={cardStyles.actionSeparator} />
              </>
            )}
            <button
              className={cardStyles.actionBtn}
              onClick={handlePreviewClick}
            >
              <i className="paperflite-eye" />
            </button>
          </div>
        )}
      </div>
      {!isSelectionMode && !readOnly && (<div className={cardStyles.cardContent}>
        <div className={cardStyles.details}>
          <div className={cardStyles.nameWrapper}>
            {isRenaming ? (
              <input
                className={cardStyles.nameInput}
                value={renameEdits[id ?? ""]?.name ?? name}
                placeholder="Storyboard name"
                onChange={(e) => id && onRenameChange(id, "storyboard", e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <Text className={cardStyles.name} text={name} lines={1} tooltip={{ placement: "auto" }} />
            )}
          </div>
          <p className={cardStyles.count}>
          last updated on{" "}
          {dateFormat(storyboard?.modifiedDate, "dd mmm yyyy")}
          </p>
        </div>
        {!readOnly && (
          <a className={cx(cardStyles.arrow)}>
            <i className="paperflite-arrow-up"></i>
          </a>
        )}
        </div>
      )}
    </div>
  );
};

interface DraggableStoryboardCardProps {
  storyboard: StoryboardItem;
}

export const DraggableStoryboardCard: React.FC<DraggableStoryboardCardProps> = ({ storyboard }) => {
  const name = _.get(storyboard, "name", "");
  const media = buildStoryboardIcon(_.get(storyboard, "page.icon"));

  return (
    <div className={cardStyles.draggableCard}>
      <Thumbnail
        className={cardStyles.icon}
        media={media}
        defaultImage="/images/collections/background.jpeg"
        width={100}
      />
      <div className={cardStyles.name}>{name}</div>
    </div>
  );
};

interface UploadingStoryboardCardProps {
  upload: UploadingStoryboard;
}

export const UploadingStoryboardCard: React.FC<UploadingStoryboardCardProps> = ({ upload }) => {
  const type = formatStoryboardType(upload.contentType);
  const statusLabel =
    upload.status === "processing"
      ? "preparing storyboard..."
      : "uploading storyboard...";

  return (
    <div className={cx(cardStyles.card, cardStyles.readonly, cardStyles.uploading)}>
      <div className={cardStyles.icon}>
        <img
          src={upload.thumbnailUrl ?? "/images/collections/background.jpeg"}
          alt=""
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).src =
              "/images/collections/background.jpeg";
          }}
        />
        <div className={cardStyles.indicatorWrapper}>
          {type && (
            <div className={cardStyles.indicators}>
              {renderStoryBoardIcon(upload.contentType)}
              <span>{type}</span>
            </div>
          )}
        </div>
      </div>
      <div className={cardStyles.cardContent}>
        <div className={cardStyles.details}>
          {/* Since in the design the name is not be there we have commented it out */}
          {/* <div className={cardStyles.nameWrapper}>
            <Text className={cardStyles.name} text={upload.name} lines={1} tooltip={{ placement: "auto" }} />
          </div> */}
          <div className={cardStyles.uploadStatus}>
            <span>{statusLabel}</span>
            <div className={cardStyles.progressTrack}>
              <div
                className={cardStyles.progressBar}
                style={{ width: `${Math.min(100, Math.max(0, upload.progress))}%` }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const renderStoryBoardIcon = (type: string) => {
  switch (type) {
    case STORYBOARD_TYPES.LANDING_PAGE:
      return <i className="paperflite-rocket" />;
    case STORYBOARD_TYPES.RESOURCE_CENTER:
      return <i className="paperflite-stack" />;
    case STORYBOARD_TYPES.INTERACTIVE_CONTENT:
    case STORYBOARD_TYPES.INTERACTIVE_CONTENT_V2:
      return <i className="paperflite-click-rate" />;
    case STORYBOARD_TYPES.SPATIAL:
      return <i className="paperflite-stream-access" />;
    case STORYBOARD_TYPES.FLIP_BOOK:
      return <i className="paperflite-book" />;
    default:
      return <i className="paperflite-rocket" />;
  }
};

export const PlaceholderStoryboardCard: React.FC<object> = () => {
  return (
    <div className={cx(cardStyles.card, cardStyles.placeholder)}>
      <div className={cx(cardStyles.icon, cardStyles.block)}></div>
      <div className={cardStyles.cardContent}>
        <div className={cardStyles.details}>
          <div className={cx(cardStyles.name, cardStyles.block)}></div>
          <p className={cx(cardStyles.count, cardStyles.block)}></p>
        </div>
      </div>
    </div>
  );
};
